/**
 * Internationalisation setup.
 *
 * Why this exists, in one paragraph:
 *   The product launched as a German-only app for German-speaking
 *   therapists and clinics. As soon as we add the first Austrian or
 *   Swiss customer (and *especially* the first English-speaking
 *   one in the EU) we need translatable strings. Doing it now is one
 *   day of work; doing it in 18 months is three weeks. This module
 *   sets up `react-i18next` with German as the default language and
 *   English as a fallback, then exposes a single `i18n` instance
 *   used by every component via the `useTranslation` hook.
 *
 * Adding a new translation key:
 *   1. Add the German string under `de.json`.
 *   2. Add the English equivalent under `en.json`.
 *   3. Use it in a component:
 *
 *        import { useTranslation } from 'react-i18next';
 *        const { t } = useTranslation();
 *        return <h1>{t('common.welcome')}</h1>;
 *
 * The migration policy lives in `docs/adr/0004-i18n.md`.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import de from './locales/de.json';
import en from './locales/en.json';

export const SUPPORTED_LANGUAGES = ['de', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      de: { translation: de },
      en: { translation: en },
    },
    fallbackLng: 'de',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    interpolation: {
      // React already escapes by default — i18next does not need to.
      escapeValue: false,
    },
    detection: {
      // Read explicit user choice first, then browser language. We persist
      // the choice in localStorage so a reload remembers it.
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'nutriflow-language',
    },
    // We only ship a small dictionary today; do not warn on missing keys
    // in production (it would litter Sentry breadcrumbs).
    saveMissing: false,
    returnNull: false,
  });

export default i18n;
